import { useEffect, useState } from 'react';
import {
  useLazyGetCampaignsQuery,
  useLazyGetLotteriesQuery,
  useLazyGetRewardHistoryQuery
} from '@/services/rewardCenter';
import {
  Campaign,
  HistoryItem,
  HistoryType,
  Lottery,
  Paged
} from '@/services/rewardCenter/types';

/** Largest page the reward center endpoints serve. */
const PAGE_LIMIT = 100;
/**
 * Upper bound on pages read. The history tabs sort, filter and group on the
 * client, so they need the whole feed; the cap only keeps a very long history
 * from turning one modal into dozens of requests.
 */
const MAX_PAGES = 10;

export interface AllPages<T> {
  items: T[];
  isLoading: boolean;
  isError: boolean;
  /**
   * More pages existed than MAX_PAGES allows: the oldest entries are missing,
   * and the list says so instead of silently ending.
   */
  isTruncated: boolean;
}

/**
 * Reads every page of a paged endpoint (up to MAX_PAGES × 100 items), in order;
 * each answer says how many remain. `key` re-runs the read when it changes.
 */
const useAllPages = <T>(
  fetchPage: (page: number) => Promise<Paged<T>>,
  key: string
): AllPages<T> => {
  const [state, setState] = useState<AllPages<T>>({
    items: [],
    isLoading: true,
    isError: false,
    isTruncated: false
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setState((previous) => ({
        ...previous,
        isLoading: true,
        isError: false
      }));
      try {
        const collected: T[] = [];
        let page = 1;
        let totalPages = 1;
        do {
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchPage(page);
          collected.push(...result.items);
          totalPages = result.totalPages;
          page += 1;
        } while (page <= totalPages && page <= MAX_PAGES && !cancelled);

        if (!cancelled) {
          setState({
            items: collected,
            isLoading: false,
            isError: false,
            isTruncated: totalPages > MAX_PAGES
          });
        }
      } catch {
        if (!cancelled)
          setState({
            items: [],
            isLoading: false,
            isError: true,
            isTruncated: false
          });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // fetchPage is rebuilt every render; `key` names what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};

/**
 * Every page of `/history?type=…`.
 *
 * The endpoint has no status filter and no cycle grouping, and TASK history
 * has no guaranteed order, so the tabs that need those work on the full list
 * and paginate it themselves. Pages come through RTK Query, so they share its
 * cache with the rest of the reward center. The history modal mounts this on
 * open, so each opening reads a fresh copy.
 */
export const useAllHistory = (type: HistoryType): AllPages<HistoryItem> => {
  const [fetchHistory] = useLazyGetRewardHistoryQuery();
  return useAllPages(
    (page) =>
      fetchHistory(
        { type, activityStatus: 'ALL', page, pageLimit: PAGE_LIMIT },
        true
      ).unwrap(),
    `history:${type}`
  );
};

/** Every completed draw, newest first. */
export const useAllLotteries = (): AllPages<Lottery> => {
  const [fetchLotteries] = useLazyGetLotteriesQuery();
  return useAllPages(
    (page) => fetchLotteries({ page, pageLimit: PAGE_LIMIT }, true).unwrap(),
    'lotteries'
  );
};

/** Every campaign, running or not. */
export const useAllCampaigns = (): AllPages<Campaign> => {
  const [fetchCampaigns] = useLazyGetCampaignsQuery();
  return useAllPages(
    (page) =>
      fetchCampaigns(
        { activityStatus: 'ALL', page, pageLimit: PAGE_LIMIT },
        true
      ).unwrap(),
    'campaigns'
  );
};
